import { z } from "zod";

export const countryCodes = [
  { code: "+61", country: "🇦🇺" },
  { code: "+64", country: "🇳🇿" },
  { code: "+1", country: "🇺🇸" },
  { code: "+44", country: "🇬🇧" },
  { code: "+33", country: "🇫🇷" },
  { code: "+49", country: "🇩🇪" },
  { code: "+81", country: "🇯🇵" },
  { code: "+82", country: "🇰🇷" },
  { code: "+86", country: "🇨🇳" },
  { code: "+91", country: "🇮🇳" },
  { code: "+351", country: "🇵🇹" },
  { code: "+353", country: "🇮🇪" },
  { code: "+39", country: "🇮🇹" },
  { code: "+34", country: "🇪🇸" },
  { code: "+52", country: "🇲🇽" },
  { code: "+55", country: "🇧🇷" },
  { code: "+7", country: "🇷🇺" },
  { code: "+46", country: "🇸🇪" },
  { code: "+41", country: "🇨🇭" },
  { code: "+31", country: "🇳🇱" },
];

export const clientFormSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters").nonempty("Full name is required"),
  email: z.string().email("Invalid email format").nonempty("Email is required"),
  phone: z.string().optional(),
  countryCode: z.string().default("+61"),
  gender: z.enum(["male", "female", "other"], {
    required_error: "Gender is required",
  }),
  dateOfBirth: z.date({
    required_error: "Date of birth is required",
  }),
  goals: z.string().optional(),
  notes: z.string().optional(),
});

export type ClientFormValues = z.infer<typeof clientFormSchema>;

export const getPlaceholder = (code: string) => {
  switch (code) {
    case "+61": return "0412 345 678";  // Australia
    case "+64": return "021 234 5678";  // New Zealand
    case "+1": return "(555) 123-4567";  // USA
    case "+44": return "07700 900123";  // UK
    default: return "Enter phone number";
  }
};

export const formatPhoneNumber = (value: string, countryCode: string) => {
  const cleaned = value.replace(/\D/g, "");
  
  if (countryCode === "+61" && cleaned.length <= 10) {
    const match = cleaned.match(/^(\d{0,4})(\d{0,3})(\d{0,3})$/);
    if (match) {
      return match
        .slice(1)
        .filter(Boolean)
        .join(" ");
    }
  } else if (countryCode === "+1" && cleaned.length <= 10) {
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      let formatted = "";
      if (match[1]) {
        formatted += `(${match[1]}`;
        if (match[2]) {
          formatted += `) ${match[2]}`;
          if (match[3]) {
            formatted += `-${match[3]}`;
          }
        }
      } else {
        formatted = cleaned;
      }
      return formatted;
    }
  } else if (countryCode === "+44" && cleaned.length <= 10) {
    const match = cleaned.match(/^(\d{0,4})(\d{0,6})$/);
    if (match) {
      return match
        .slice(1)
        .filter(Boolean)
        .join(" ");
    }
  }
  
  if (cleaned.length > 0) {
    if (cleaned.length > 3) {
      let formatted = cleaned.substring(0, 3) + " ";
      if (cleaned.length > 6) {
        formatted += cleaned.substring(3, 6) + " " + cleaned.substring(6, 10);
      } else {
        formatted += cleaned.substring(3);
      }
      return formatted;
    }
  }
  
  return cleaned;
};

export const calculateAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};
